import { homedir } from "node:os";
import { join } from "node:path";
import { isCarrierMcpHost, isProductionMcpUrl, lineMentionsProductionMcp } from "./mcp-url.js";
import { pluginStatus, MCP_NAME, MCP_URL } from "./plugin.js";
import { run, which } from "./exec.js";
import { exists, readFile } from "./fsx.js";
import { claudeHome } from "./paths.js";

export type AuthMode = "oauth-first-use" | "api-key" | "ocs-token" | "none";

export interface CarrierStatus {
  pluginInstalled: boolean;
  pluginVersion?: string;
  pluginDir: string;
  claudeFound: boolean;
  /** True when config or `claude mcp list` reports a carrier server. */
  mcpRegistered: boolean;
  /** True when registered MCP points at the production zero-cred URL. */
  mcpUrlMatches: boolean;
  mcpDetail?: string;
  /** Env credential present for headless `carrier ask` / stdio. */
  tokenPresent: boolean;
  tokenKind: "api-key" | "ocs-token" | "none";
  authMode: AuthMode;
  /** Human-readable next steps — always non-empty. */
  nextSteps: string[];
}

function detectEnvToken(): { present: boolean; kind: "api-key" | "ocs-token" | "none" } {
  const api =
    process.env.CARRIER_API_KEY?.trim() ||
    process.env.CARRIER_ORG_API_KEY?.trim() ||
    "";
  if (api.startsWith("ak_") || api.length > 0) {
    return { present: true, kind: "api-key" };
  }
  const ocs =
    process.env.ESIMVAULT_API_TOKEN?.trim() ||
    process.env.CARRIER_OCS_API_TOKEN?.trim() ||
    "";
  if (ocs) return { present: true, kind: "ocs-token" };
  return { present: false, kind: "none" };
}

type McpEntry = { url?: string; type?: string; command?: string; args?: string[] };
type McpHit = { registered: boolean; urlMatches: boolean; detail?: string; rank: number };

function rankHit(name: string, url: string): number {
  // Higher is better — prefer exact "carrier" + production URL.
  let rank = 0;
  if (name.toLowerCase() === MCP_NAME) rank += 100;
  else if (/^carrier[-_]?/i.test(name) && !/test|staging|stg|dev|local/i.test(name)) rank += 40;
  else if (/carrier/i.test(name) && !/test|staging|stg|dev|local/i.test(name)) rank += 20;
  else if (/carrier/i.test(name)) rank += 5; // test/staging variants
  if (isProductionMcpUrl(url)) rank += 50;
  else if (isCarrierMcpHost(url)) rank += 25;
  return rank;
}

function scanMcpServers(servers: Record<string, McpEntry> | undefined): McpHit | null {
  if (!servers || typeof servers !== "object") return null;
  let best: McpHit | null = null;
  for (const [name, entry] of Object.entries(servers)) {
    if (!/carrier/i.test(name) && !(typeof entry?.url === "string" && /carrier\.llc/i.test(entry.url))) {
      continue;
    }
    const url = typeof entry?.url === "string" ? entry.url : "";
    const rank = rankHit(name, url);
    if (rank < 20 && !isCarrierMcpHost(url)) {
      // Ignore pure test/staging name matches without a production URL.
      continue;
    }
    const urlMatches = isProductionMcpUrl(url);
    const hit: McpHit = {
      registered: true,
      urlMatches,
      detail: url ? `${name}: ${url}` : `${name}: ${entry?.command ?? "configured"}`,
      rank,
    };
    if (!best || hit.rank > best.rank) best = hit;
  }
  return best;
}

/** Fast path: read Claude config files instead of spawning a health probe. */
async function probeMcpFromConfig(): Promise<{
  hit: { registered: boolean; urlMatches: boolean; detail?: string } | null;
  /** True when we successfully parsed at least one Claude config file. */
  configSeen: boolean;
}> {
  const candidates = [
    join(homedir(), ".claude.json"),
    join(claudeHome(), "settings.json"),
    join(claudeHome(), ".mcp.json"),
    join(process.cwd(), ".mcp.json"),
  ];
  let best: McpHit | null = null;
  let configSeen = false;
  for (const file of candidates) {
    if (!(await exists(file))) continue;
    try {
      const json = JSON.parse(await readFile(file, "utf8")) as {
        mcpServers?: Record<string, McpEntry>;
        projects?: Record<string, { mcpServers?: Record<string, McpEntry> }>;
      };
      configSeen = true;
      const direct = scanMcpServers(json.mcpServers);
      if (direct && (!best || direct.rank > best.rank)) best = direct;
      if (json.projects) {
        for (const proj of Object.values(json.projects)) {
          const hit = scanMcpServers(proj?.mcpServers);
          if (hit && (!best || hit.rank > best.rank)) best = hit;
        }
      }
    } catch {
      /* ignore malformed */
    }
  }
  if (!best) return { hit: null, configSeen };
  return {
    hit: { registered: best.registered, urlMatches: best.urlMatches, detail: best.detail },
    configSeen,
  };
}

/**
 * Probe Claude Code for a registered Carrier MCP.
 * Prefers local config files; only shells out to `claude mcp list` when no config is readable.
 */
export async function probeMcpRegistration(): Promise<{
  registered: boolean;
  urlMatches: boolean;
  detail?: string;
}> {
  const { hit, configSeen } = await probeMcpFromConfig();
  if (hit) return hit;
  // Config present but no production Carrier MCP — trust that over a slow health probe.
  if (configSeen) {
    return {
      registered: false,
      urlMatches: false,
      detail: "carrier not registered (no production MCP URL in Claude config)",
    };
  }

  const claudeFound = await which("claude");
  if (!claudeFound) {
    return {
      registered: false,
      urlMatches: false,
      detail: "claude CLI not on PATH",
    };
  }
  // Health probes can hang when many MCP servers are configured — cap wait.
  const r = await run("claude", ["mcp", "list"], { timeoutMs: 5_000 });
  const out = `${r.stdout}\n${r.stderr}`;
  if (!r.ok && !out.trim()) {
    return { registered: false, urlMatches: false, detail: "claude mcp list failed or timed out" };
  }
  if (/timeout after/i.test(r.stderr) && !/carrier/i.test(out)) {
    return {
      registered: false,
      urlMatches: false,
      detail: "claude mcp list timed out — run `claude mcp list` manually",
    };
  }

  // Lines look like: `carrier: https://mcp.carrier.llc/mcp (HTTP) - ✔ Connected`
  // or `carrier: … - ! Needs authentication`
  const lines = out.split("\n").map((l) => l.trim()).filter(Boolean);
  const carrierLine =
    lines.find((l) => new RegExp(`^${MCP_NAME}\\s*:`, "i").test(l)) ??
    lines.find((l) => /carrier/i.test(l) && /mcp\.carrier\.llc/i.test(l));

  if (!carrierLine) {
    return { registered: false, urlMatches: false, detail: "carrier not in claude mcp list" };
  }

  const urlMatches = lineMentionsProductionMcp(carrierLine);
  return { registered: true, urlMatches, detail: carrierLine.slice(0, 200) };
}

function buildNextSteps(s: Omit<CarrierStatus, "nextSteps">): string[] {
  const steps: string[] = [];

  if (!s.claudeFound) {
    steps.push("Install Claude Code (https://claude.ai/code), then re-run: carrier plugin install");
  } else if (!s.pluginInstalled) {
    steps.push("Install the Carrier plugin: carrier plugin install");
  } else if (!s.mcpRegistered) {
    steps.push(`Register the zero-cred MCP URL: claude mcp add --transport http ${MCP_NAME} ${MCP_URL}`);
  } else if (!s.mcpUrlMatches) {
    steps.push(`Update MCP to production URL: claude mcp add --transport http ${MCP_NAME} ${MCP_URL}`);
  }

  if (s.mcpRegistered || s.pluginInstalled) {
    steps.push(
      "Talk to your fleet in Claude — first tool call opens Clerk OAuth (Google / GitHub / email). No API token needed.",
    );
  } else {
    steps.push("Create a free account (browser): carrier open signup");
    steps.push("Or sign in: carrier open signin");
  }

  if (!s.tokenPresent) {
    steps.push(
      "Optional headless key: Console → Settings → API Keys (ak_…) then export CARRIER_API_KEY=ak_… for `carrier ask`.",
    );
  } else {
    steps.push('Try a natural-language fleet query: carrier ask "show fleet health"');
  }

  steps.push("Scaffold a white-label storefront anytime: carrier site create");
  return steps;
}

/** Collect install / auth / MCP status for the TUI home and `carrier status`. */
export async function gatherStatus(): Promise<CarrierStatus> {
  const [plugin, mcp, claudeFound] = await Promise.all([
    pluginStatus(),
    probeMcpRegistration(),
    which("claude"),
  ]);
  const token = detectEnvToken();

  let authMode: AuthMode = "none";
  if (token.kind === "api-key") authMode = "api-key";
  else if (token.kind === "ocs-token") authMode = "ocs-token";
  else if (mcp.registered || plugin.installed) authMode = "oauth-first-use";

  const base = {
    pluginInstalled: plugin.installed,
    pluginVersion: plugin.version,
    pluginDir: plugin.dir,
    claudeFound,
    mcpRegistered: mcp.registered,
    mcpUrlMatches: mcp.urlMatches,
    mcpDetail: mcp.detail,
    tokenPresent: token.present,
    tokenKind: token.kind,
    authMode,
  };

  return { ...base, nextSteps: buildNextSteps(base) };
}

/** Resolve a bearer-style credential from the environment only (synchronous). */
export function resolveCliToken(): string | null {
  const api =
    process.env.CARRIER_API_KEY?.trim() ||
    process.env.CARRIER_ORG_API_KEY?.trim() ||
    "";
  if (api) return api;
  const ocs =
    process.env.ESIMVAULT_API_TOKEN?.trim() ||
    process.env.CARRIER_OCS_API_TOKEN?.trim() ||
    "";
  return ocs || null;
}
